import { useState } from 'react';

import type {
  BroadcastContent,
  ConnectingLineDelayProperties,
  DelayBroadcastPattern,
} from '../../../api/types';
import {
  PHRASE_TEMPLATE_CONNECTING_DELAY_DEPARTURE_FORECAST,
  PHRASE_TEMPLATE_CONNECTING_DELAY_PREFIX,
} from '../../../data/phraseTemplates';
import {
  CONNECTING_DEPARTURE_FORECAST_PATTERN_DEFAULT_SSML,
  CONNECTING_GRANULARITY_DEFAULT_SSML,
  isDepartureForecastMode,
} from '../../../utils/broadcastContents';
import { createDefaultPattern, resolveSsmlTemplateOnGranularityChange } from '../logic';
import { AudioPreviewField } from '../parts/AudioPreviewField';
import {
  CONNECTING_DEPARTURE_FORECAST_PLACEHOLDERS,
  CONNECTING_TRANSFER_AWARE_PLACEHOLDERS,
} from '../placeholders';
import { deriveContentSampleOverrides, deriveSampleDelaySeconds } from '../previewSample';
import type { PreviewSsmlHandler } from '../types';
import { DelayPatternRow } from './DelayPatternRow';

/**
 * 接続線遅延 (transfer_aware / departure_forecast モード) 用の表形式エディタ。
 *
 * `departure_forecast` (接続線の発車見込み案内 / SBI-5) では「放送粒度」列を隠す。
 * 粒度は文面に影響しないため、新規行は既定粒度で作り、保存時に固定値へ揃える
 * (`utils/broadcastContents.ts` の `pinDepartureForecastGranularity`)。
 * 「遅れ分数」の範囲は「どの遅れのときに案内するか」を表し、該当する行が無ければ案内しない。
 *
 * 自列車遅延の `SelfTrainDelayFields` と同じ構造で、遅れ分数の幅ごとに 1 行ずつ
 * 「遅れ分数 / 放送粒度 / 放送パターン (SSMLテンプレート) / 行操作 (＋−)」を表示する。
 * 行コンポーネント (`DelayPatternRow`) は自列車遅延と共通化しているため、testid プレフィックスを
 * `connecting-line-delay-ssml-template` に差し替えて自列車遅延の <textarea> と区別する。
 */
export function ConnectingLineDelayPatternsTable({
  props,
  threshold,
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  onChange,
}: {
  props: Partial<ConnectingLineDelayProperties>;
  threshold: number;
  /** プレビュー対象の放送コンテンツ (Issue #439) */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  /** 親フォームで選択中の Polly VoiceId (実放送と同じ声色で試聴するため) */
  parentVoiceId?: string;
  /** 親コンテンツの `playback_rate` (プレビュー音声にも反映する) */
  playbackRate?: number;
  /** 音声プレビュー処理。省略時はプレビュー UI を表示しない */
  onPreviewSsml?: PreviewSsmlHandler;
  onChange: (props: ConnectingLineDelayProperties) => void;
}): React.JSX.Element {
  /*
   * プレビューで採用された遅延パターン行の index (Issue #439)。
   * サーバの `select_delay_pattern()` の結果をそのまま反映してハイライトする。
   */
  const [matchedPatternIndex, setMatchedPatternIndex] = useState<number | null>(null);
  const isDepartureForecast = isDepartureForecastMode(props.delay_judgement_mode);
  const existingPatterns: DelayBroadcastPattern[] = Array.isArray(props.delay_patterns)
    ? props.delay_patterns
    : [];
  const patterns: DelayBroadcastPattern[] =
    existingPatterns.length > 0
      ? existingPatterns
      : [createDefaultPattern(CONNECTING_GRANULARITY_DEFAULT_SSML)];

  /** delay_patterns を更新し、必須プロパティ (route_id / threshold) を維持して onChange を呼ぶ */
  const emit = (nextPatterns: DelayBroadcastPattern[]): void => {
    onChange({
      ...props,
      route_id: props.route_id ?? '',
      delay_threshold_seconds: threshold,
      delay_patterns: nextPatterns,
    } as ConnectingLineDelayProperties);
  };

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = patterns.slice();
    const current = next[index]!;
    // Issue #527: 粒度が変わっても **デフォルト文を実値で書き込まない**
    // (空欄 = 定型文マスタへ委譲。放送側が `connecting_delay_{粒度}` を引く)。
    // 未編集 (空欄 or いずれかの粒度のデフォルト文と一致) なら空欄に保ち、
    // ユーザーがカスタム編集した文面はそのまま維持する。
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        ssml_template: resolveSsmlTemplateOnGranularityChange(
          current.ssml_template,
          CONNECTING_GRANULARITY_DEFAULT_SSML,
        ),
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    emit(next);
  };

  const addPatternAfter = (index: number): void => {
    const next = patterns.slice();
    next.splice(index + 1, 0, createDefaultPattern(CONNECTING_GRANULARITY_DEFAULT_SSML));
    emit(next);
  };

  const removePattern = (index: number): void => {
    if (patterns.length <= 1) return;
    const next = patterns.slice();
    next.splice(index, 1);
    emit(next);
  };

  return (
    <div className="connecting-line-delay-patterns" data-testid="connecting-line-delay-patterns">
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            {isDepartureForecast ? null : (
              <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
                放送粒度
              </th>
            )}
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (SSMLテンプレート)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {patterns.map((pattern, i) => (
            <DelayPatternRow
              languages={content?.languages}
              key={i}
              index={i}
              pattern={pattern}
              canRemove={patterns.length > 1}
              ssmlTestIdPrefix="connecting-line-delay-ssml-template"
              placeholderTemplates={
                isDepartureForecast
                  ? CONNECTING_DEPARTURE_FORECAST_PATTERN_DEFAULT_SSML
                  : CONNECTING_GRANULARITY_DEFAULT_SSML
              }
              templateKind={isDepartureForecast ? 'departure_forecast' : 'connecting'}
              // Issue #527: 空欄時の placeholder を定型文マスタ
              // (`connecting_delay_{粒度}`) の文面にする (未登録ならコード内既定)。
              // SBI-5: 発車見込み案内は粒度別ではなく単一のマスタ ID
              // (`connecting_delay_departure_forecast`) を引く。
              granularityTemplatePrefix={
                isDepartureForecast ? undefined : PHRASE_TEMPLATE_CONNECTING_DELAY_PREFIX
              }
              fixedTemplateId={
                isDepartureForecast
                  ? PHRASE_TEMPLATE_CONNECTING_DELAY_DEPARTURE_FORECAST
                  : undefined
              }
              hideGranularity={isDepartureForecast}
              // Issue #439: 行ごとに「▶ この文面を試聴」を出す。
              // サンプル条件の既定値はコンテンツ設定 (乗換駅 / 線名 / 方面) から導出し、
              // 遅延分数だけは **この行が選ばれる値** で上書きする
              // (行ごとに試聴する UI なので、その行の境界値で鳴らないと意味がない)。
              // ユーザーが条件パネルで指定した値は上書きされない (`usePreviewSample`)。
              previewSlot={
                content ? (
                  <AudioPreviewField
                    content={content}
                    parentVoiceId={parentVoiceId}
                    playbackRate={playbackRate}
                    onPreviewSsml={onPreviewSsml}
                    testIdSuffix={`connecting-line-delay-${contentIndex}-${i}`}
                    label="▶ この文面を試聴"
                    placeholders={
                      isDepartureForecast
                        ? CONNECTING_DEPARTURE_FORECAST_PLACEHOLDERS
                        : CONNECTING_TRANSFER_AWARE_PLACEHOLDERS
                    }
                    sampleOverrides={{
                      ...deriveContentSampleOverrides(content),
                      delaySeconds: deriveSampleDelaySeconds(pattern),
                    }}
                    // 発車見込み案内 (SBI-6) も試聴できる。時刻 / 行先はプレビュー条件パネルで指定する。
                    disabled={(pattern.ssml_template ?? '').trim() === ''}
                    disabledReason="SSML を入力すると試聴できます"
                    onMatchedPatternIndexChange={setMatchedPatternIndex}
                  />
                ) : null
              }
              highlighted={matchedPatternIndex === i}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>

      <p className="hint" style={{ margin: '8px 0 4px' }}>
        各行の「遅れ分数」の範囲に隙間が生じないよう注意して設定してください。
        各行の「＋」で行を追加、「−」で行を削除できます。 SSML 欄は
        <strong>空欄でも構いません</strong>。空欄のときは定型文設定 (定型文設定画面で編集)
        の粒度別デフォルト文面が使われます (薄字で表示されている文面)。
        定型文設定を編集すると、空欄のコンテンツすべての放送内容が一括で変わります。
      </p>
      {isDepartureForecast ? (
        <p
          className="hint"
          style={{ margin: '4px 0' }}
          data-testid="departure-forecast-patterns-hint"
        >
          「発車の見込み」では遅延分数を読み上げません。「遅れ分数」は
          <strong>どれだけ遅れているときに発車見込みを案内するか</strong>
          を表します (該当する行が無いときは案内しません)。定時 (遅れ 0 分) も案内したい場合は「0
          分以上」の行にしてください。放送粒度は使用しないため表示していません。
        </p>
      ) : null}
    </div>
  );
}
